import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useRegion } from '../auth/RegionContext';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { JustifiedAction } from '../actions/JustifiedAction';
import { REASON_CODE_OPTIONS } from '../suspensions/suspensionState';
import {
  LINK_SIGNAL_LABEL,
  type BanEvasionAnchor as Anchor,
  type SuspectedLink,
} from './banEvasionModel';

/**
 * One banned anchor in the ban-evasion surface (D-ban-evasion): the banned account
 * at the head, with the suspected same-person ACTIVE accounts beneath, each showing
 * the matched signals as the BASIS (not an opaque score) and the two actions.
 *
 * The action set is detection + dismiss + the inherited ban (no new value-moving
 * action). DISMISS (users:dismiss_link, fraud) records a value-neutral decision that
 * suppresses the link until a stronger match re-raises it. BAN reuses the EXACT
 * fraud-queue ban (users:ban with reason codes) against the linked account: a status
 * write, no balance, clear of the authorise-execute line.
 */

const LinkRow = ({
  anchor,
  link,
  canDismiss,
  canBan,
}: {
  anchor: Anchor;
  link: SuspectedLink;
  canDismiss: boolean;
  canBan: boolean;
}) => (
  <Box
    data-testid={`ban-link-${anchor.anchorId}-${link.linkedId}`}
    sx={{ py: 1.5 }}
  >
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      useFlexGap
      flexWrap="wrap"
    >
      <Box sx={{ minWidth: 200 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {link.linkedName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {link.linkedEmail} | {link.linkedMarket.toUpperCase()} | joined{' '}
          {link.linkedCreatedAt.slice(0, 10)}
        </Typography>
      </Box>
      {link.createdAfterAnchor && (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label="Created after the ban"
        />
      )}
      <Typography variant="caption" color="text.secondary">
        {link.strength} {link.strength === 1 ? 'signal' : 'signals'}
      </Typography>
    </Stack>

    {/* The BASIS: the signals that matched, shown as facts, never as a score. */}
    <Stack
      direction="row"
      spacing={1}
      sx={{ mt: 1 }}
      useFlexGap
      flexWrap="wrap"
      data-testid={`ban-signals-${anchor.anchorId}-${link.linkedId}`}
    >
      {link.signals.map((s) => (
        <Chip
          key={s}
          size="small"
          variant="outlined"
          color="error"
          label={LINK_SIGNAL_LABEL[s]}
        />
      ))}
    </Stack>

    <Stack
      direction="row"
      spacing={2}
      sx={{ mt: 1.5 }}
      useFlexGap
      flexWrap="wrap"
    >
      {canBan && (
        <JustifiedAction
          resource="users"
          capability="users:ban"
          id={link.linkedId}
          label="Ban linked account"
          color="error"
          dialogTitle="Ban this linked account"
          dialogIntro="Confirmed evasion. Pick a reason code. This is the same ban as the fraud queue, a status only. Record why."
          reasonCodes={REASON_CODE_OPTIONS}
          buildData={(reason, _member, code) => ({
            fraudStatus: 'banned',
            suspensionEndsAt: null,
            suspensionReasonCode: code,
            fraudReason: reason,
          })}
          previousData={{
            id: link.linkedId,
            fraudStatus: 'active',
            suspensionEndsAt: null,
            fraudReason: link.linkedFraudReason,
            suspensionReasonCode: link.linkedSuspensionReasonCode,
            appealStatus: link.linkedAppealStatus,
          }}
          successMessage="Account banned"
        />
      )}
      {canDismiss && (
        <JustifiedAction
          resource="users"
          capability="users:dismiss_link"
          id={link.linkedId}
          label="Dismiss link"
          color="inherit"
          dialogTitle="Dismiss this link"
          dialogIntro="Not evasion (a household, a coincidence). This clears the link until a stronger signal match re-raises it. Record why."
          buildData={() => ({
            linkDismissals: [
              ...link.linkedDismissals,
              { anchorId: anchor.anchorId, signals: link.signals },
            ],
          })}
          previousData={{
            id: link.linkedId,
            linkDismissals: link.linkedDismissals,
          }}
          successMessage="Link dismissed"
        />
      )}
    </Stack>
  </Box>
);

export const BanEvasionAnchor = ({ anchor }: { anchor: Anchor }) => {
  const { accessibleRegions } = useRegion();
  const { role } = useAccessContext();
  const ctx = { role, accessibleRegions };
  const can = (capability: Parameters<typeof hasAccess>[1]['capability']) =>
    hasAccess(ctx, { capability });
  const canDismiss = can('users:dismiss_link');
  const canBan = can('users:ban');

  return (
    <Card
      variant="outlined"
      data-testid={`ban-anchor-${anchor.anchorId}`}
      sx={{ mb: 2 }}
    >
      <CardContent>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {anchor.anchorName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {anchor.anchorEmail} | {anchor.anchorMarket.toUpperCase()}
            </Typography>
          </Box>
          <Chip size="small" color="error" variant="filled" label="Banned" />
          <Chip
            size="small"
            variant="outlined"
            label={`${anchor.links.length} suspected ${
              anchor.links.length === 1 ? 'link' : 'links'
            }`}
          />
        </Stack>
        {anchor.anchorReason && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ mt: 1 }}
          >
            Ban reason: {anchor.anchorReason}
          </Typography>
        )}

        <Divider sx={{ my: 1.5 }} />

        {anchor.links.map((link) => (
          <LinkRow
            key={link.linkedId}
            anchor={anchor}
            link={link}
            canDismiss={canDismiss}
            canBan={canBan}
          />
        ))}
      </CardContent>
    </Card>
  );
};
